import { useCallback, useEffect, useState } from "react";

import { useApi } from "../useApi";

/** Windows the analytics endpoint accepts. */
export type AnalyticsRange = "today" | "7d" | "30d" | "90d" | "all";

export const ANALYTICS_RANGE_OPTIONS: ReadonlyArray<{
  value: AnalyticsRange;
  label: string;
}> = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

export const DEFAULT_ANALYTICS_RANGE: AnalyticsRange = "30d";

/**
 * Headline figures for the selected window.
 *
 * Several fields are nullable on purpose, and null does not mean zero:
 * - `estimatedCostUsd` is null when nothing in the window has a known rate.
 * - `avgSessionSeconds` is null when no session was observed end to end.
 * - `questionsPerUser` is null when nobody asked anything.
 * - `priorActiveUsers` / `priorQuestionsAsked` are null when there is no
 *   preceding window to compare against.
 *
 * Rendering any of those as 0 would state something the data does not support.
 */
export interface AnalyticsKpis {
  activeUsers: number;
  questionsAsked: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedCostUsd: number | null;
  /** True when some usage came from a model with no known rate. */
  costIsPartial: boolean;
  unpricedModelIds: string[];
  avgSessionSeconds: number | null;
  observedSessions: number;
  questionsPerUser: number | null;
  priorActiveUsers: number | null;
  priorQuestionsAsked: number | null;
}

export interface AnalyticsDailyPoint {
  /** Calendar day in UTC, `YYYY-MM-DD`. */
  date: string;
  questions: number;
  activeUsers: number;
}

export interface AnalyticsModelRow {
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  /** Fraction of the window's tokens, 0 to 1. */
  tokenShare: number;
  estimatedCostUsd: number | null;
  /** False when this model has no known rate, making its cost unknown. */
  isPriced: boolean;
}

export interface AnalyticsUserRow {
  userId: string;
  displayName: string;
  questions: number;
  sessions: number;
  avgSessionSeconds: number | null;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedCostUsd: number | null;
  costIsPartial: boolean;
}

export interface UserAnalyticsResponse {
  range: string;
  /** Null for the all-time window, which has no bounded start. */
  rangeStart: string | null;
  rangeEnd: string | null;
  /** Day boundaries are UTC, which is not the viewer's midnight. */
  timezone: string;
  kpis: AnalyticsKpis;
  /** Empty for the all-time window: lifetime totals carry no daily trend. */
  dailySeries: AnalyticsDailyPoint[];
  modelBreakdown: AnalyticsModelRow[];
  users: AnalyticsUserRow[];
}

const FALLBACK_ERROR = "Failed to load user analytics.";

/**
 * Data access for the admin "User Analytics" page.
 *
 * Reads aggregated usage for a window from `GET /admin/user-analytics` and
 * re-reads it whenever the window changes.
 *
 * On failure the figures are withheld rather than kept: leaving the previous
 * window's numbers on screen next to an error invites reading them as the
 * numbers for the window now selected.
 */
const useAdminUserAnalytics = () => {
  const [data, setData] = useState<UserAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<AnalyticsRange>(DEFAULT_ANALYTICS_RANGE);
  // Bumped to re-run the load for the same window.
  const [reloadCount, setReloadCount] = useState(0);

  const { apiGet } = useApi({ isAdmin: true });

  useEffect(() => {
    // Scoped to this run of the effect, never shared: a flag held across runs
    // would be reset by the next one, letting a superseded request through to
    // overwrite fresher data.
    let superseded = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await apiGet<UserAnalyticsResponse>(
          "/admin/user-analytics",
          { queryParams: { range } }
        );
        if (superseded) {
          return;
        }
        if (!response) {
          // An absent body is a failure. Presenting it as a window with no
          // usage would be indistinguishable from a genuinely quiet period.
          setData(null);
          setError(FALLBACK_ERROR);
          return;
        }
        setData(response);
        setError(null);
      } catch (caught) {
        if (superseded) {
          return;
        }
        console.error(caught);
        const message = (caught as { message?: string } | null)?.message;
        setData(null);
        setError(message ? `${FALLBACK_ERROR} ${message}` : FALLBACK_ERROR);
      } finally {
        if (!superseded) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      superseded = true;
    };
  }, [apiGet, range, reloadCount]);

  const refresh = useCallback(() => {
    setReloadCount((count) => count + 1);
  }, []);

  return {
    data,
    loading,
    error,
    range,
    setRange,
    refresh,
  };
};

export default useAdminUserAnalytics;
